import React, { useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  StaysSyncError,
  SyncDecisionEdits,
  SyncReview,
  SyncReviewConflict,
  decideSyncReview,
} from '../../../../api/stays-sync-client';
import { AppButton } from '../../../button/app-button';
import { CountedTextarea } from '../../../counted-textarea/counted-textarea';
import { Field, FieldError, Input, Label } from '../../../form-kit';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { SegmentedControl } from '../../../segmented-control/segmented-control';
import { StatusPill } from '../../../status-pill/status-pill';
import { ActionWithReason } from '../../dashboard/dashboard-shared-components/disabled-reason';
import { Collapse, StatusLabel } from '../../../../motion';
import { formatDayKey } from '../../../../function-utils/building-time';
import toast from '../../../../utils/toast';
import { stayHref } from '../listing/stays-listing-shell';
import {
  datesLine,
  decidedLine,
  kindPill,
  providerName,
  shortRange,
  whenText,
} from '../../../../utils/stays-sync';
import { Actions, Card, Muted, RowLine, RowTitle } from './sync.styled';

/**
 * ONE CALENDAR CHANGE (plan S5, screens §2; R-S5.24). The pill by kind, the
 * listing, when it was found (the reader's clock), the dates as day keys
 * (never re-zoned), what the booking site said, and the host's answer:
 *   new               → Add stay (or Add as blocked dates) / Ignore
 *   date_update       → Move the stay / Keep the old dates
 *   conflict          → Import with new dates (after a date edit) / Ignore
 *   missing_from_feed → Cancel the stay (asks first) / Keep it
 *   cancelled_by_…    → Cancel the stay (asks first) / Keep it
 * Guest details can be added to a new stay (the calendar never carries a
 * name). A clash found while approving turns the card into the clash shape in
 * place; a change someone else answered is reloaded. Read-only in "Recent"
 * and without stays:manage. Every button's name carries the dates.
 */

export interface SyncReviewCardProps {
  review: SyncReview;
  canManage: boolean;
  /** The reader's clock (R-T12) for "Found 14:05". */
  zone: string;
  now: Date;
  /** Show the listing name (the full page; the listing tab knows it). */
  showListing?: boolean;
  /** After an answer, or when the item is stale: the list reloads. */
  onChanged: () => void;
}

interface Guest {
  lead_first_name: string;
  lead_last_name: string;
  lead_email: string;
  lead_phone: string;
  party_size: string;
  notes: string;
}

const NO_GUEST: Guest = { lead_first_name: '', lead_last_name: '', lead_email: '', lead_phone: '', party_size: '', notes: '' };

const conflictLine = (siteName: string, c: SyncReviewConflict): string =>
  c.type === 'block'
    ? `Double booking: this ${siteName} stay and closed dates (${formatDayKey(c.check_in)} → ${formatDayKey(c.check_out)}) hold the same nights.`
    : `Double booking: this ${siteName} stay and another stay (${formatDayKey(c.check_in)} → ${formatDayKey(c.check_out)}) hold the same nights.`;

export const SyncReviewCard: React.FC<SyncReviewCardProps> = ({ review, canManage, zone, now, showListing = false, onChanged }) => {
  const uid = useId();
  const [kind, setKind] = useState(review.kind);
  const [conflicts, setConflicts] = useState<SyncReviewConflict[]>(review.conflicts || []);
  const [as, setAs] = useState<'reservation' | 'block'>(review.proposed.as === 'block' ? 'block' : 'reservation');
  const [guestOpen, setGuestOpen] = useState(false);
  const [guest, setGuest] = useState<Guest>(NO_GUEST);
  const [dates, setDates] = useState({ check_in: review.proposed.check_in, check_out: review.proposed.check_out });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<null | 'approve' | 'dismiss'>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const guard = useRef(false);

  const site = providerName(review.provider);
  const pending = review.state === 'pending';
  const can = canManage && pending;
  const pill = kindPill(kind, review.provider);
  const ask = review.proposed.as === 'ask';
  const isNewShape = (kind === 'new' || kind === 'conflict') && !review.reservation && !review.proposed.restore;
  const datesEdited = dates.check_in !== review.proposed.check_in || dates.check_out !== review.proposed.check_out;
  const range = shortRange(dates.check_in, dates.check_out);
  const setG = (k: keyof Guest, v: string) => setGuest(g => ({ ...g, [k]: v }));

  const send = async (decision: 'approve' | 'dismiss') => {
    if (guard.current) return;
    guard.current = true;
    setBusy(decision);
    setErrors({});
    const edits: SyncDecisionEdits = {};
    if (decision === 'approve') {
      if (ask && isNewShape) edits.as = as;
      if (datesEdited) Object.assign(edits, dates);
      if (isNewShape && as === 'reservation') {
        for (const k of ['lead_first_name', 'lead_last_name', 'lead_email', 'lead_phone', 'notes'] as const) {
          if (guest[k].trim()) edits[k] = guest[k].trim();
        }
        if (guest.party_size.trim()) edits.party_size = Number(guest.party_size);
      }
    }
    try {
      await decideSyncReview(review.id, {
        decision,
        version: review.version,
        ...(Object.keys(edits).length ? { edits } : {}),
      });
      toast.success(
        decision === 'dismiss'
          ? 'Change ignored'
          : kind === 'missing_from_feed' || kind === 'cancelled_by_provider'
            ? `Stay ${range} cancelled`
            : as === 'block' && isNewShape
              ? `Dates ${range} closed`
              : `Stay ${range} saved`
      );
      onChanged();
    } catch (err) {
      const x = err as StaysSyncError;
      if (x && x.code === 'REVIEW_CHANGED') {
        toast.error('Someone else decided this a moment ago');
        onChanged();
      } else if (x && x.code === 'RESERVATION_OVERLAP') {
        const found = Array.isArray(x.extra.conflicts) ? (x.extra.conflicts as SyncReviewConflict[]) : [];
        setKind('conflict');
        setConflicts(found);
      } else if (x && x.status === 400 && Object.keys(x.fields || {}).length) {
        setErrors(x.fields);
        setGuestOpen(true);
      } else {
        toast.error(x && x.status > 0 && x.message ? x.message : 'Your answer could not be saved. Please try again.');
      }
    } finally {
      guard.current = false;
      setBusy(null);
    }
  };

  const reviewTitle = `${pill.text} · ${datesLine(review.proposed.check_in, review.proposed.check_out)}`;

  const primary = (() => {
    if (!can) return null;
    switch (kind) {
      case 'new':
        if (review.proposed.restore) {
          return { label: 'Restore the stay', aria: `Restore the stay ${range}`, variant: 'primary' as const };
        }

        return as === 'block' && isNewShape
          ? { label: 'Add as blocked dates', aria: `Add ${range} as blocked dates`, variant: 'primary' as const }
          : { label: 'Add stay', aria: `Add the stay ${range}`, variant: 'primary' as const };
      case 'date_update':
        return { label: 'Move the stay', aria: `Move the stay to ${range}`, variant: 'primary' as const };
      case 'conflict':
        return { label: 'Import with new dates', aria: `Import the stay with new dates ${range}`, variant: 'primary' as const };
      default:
        return { label: 'Cancel the stay', aria: `Cancel the stay ${range}`, variant: 'destructive' as const };
    }
  })();
  const secondary =
    kind === 'date_update'
      ? { label: 'Keep the old dates', aria: `Keep the old dates, ignore ${range}`, variant: 'ghost' as const }
      : kind === 'missing_from_feed' || kind === 'cancelled_by_provider'
        ? { label: 'Keep it', aria: `Keep the stay ${range}`, variant: 'secondary' as const }
        : { label: 'Ignore', aria: `Ignore ${range}`, variant: 'ghost' as const };
  const needsDates = kind === 'conflict' && !datesEdited;

  return (
    <Card data-testid={`sync-review-${review.id}`} data-kind={kind} aria-label={reviewTitle}>
      <RowLine>
        <RowTitle>{site}</RowTitle>
        <StatusPill tone={pill.tone} dot>
          <StatusLabel
            state={kind === review.kind ? 'found' : 'clash'}
            labels={{ found: kindPill(review.kind, review.provider).text, clash: kindPill('conflict', review.provider).text }}
          />
        </StatusPill>
        {showListing && review.listing.name ? <span>{review.listing.name}</span> : null}
        {review.created_at ? <span data-at={review.created_at}>{`Found ${whenText(review.created_at, zone, now)}`}</span> : null}
      </RowLine>

      <Muted as="div">
        <div>{datesLine(review.proposed.check_in, review.proposed.check_out)}</div>
        {kind === 'date_update' && review.previous ? (
          <div>{`Was ${datesLine(review.previous.check_in, review.previous.check_out)}`}</div>
        ) : null}
        {kind === 'conflict'
          ? conflicts.map(c => (
            <div key={`${c.type}-${c.id}`}>
              {conflictLine(site, c)}{' '}
              {c.type === 'reservation' ? <Link to={stayHref(review.listing.id, c.id)}>Open the other stay</Link> : null}
            </div>
          ))
          : null}
        {kind === 'missing_from_feed' ? (
          <div>{`${site} no longer lists this stay. ${site} removes a stay when it is cancelled, but a calendar can also drop a stay by mistake.`}</div>
        ) : null}
        {kind === 'cancelled_by_provider' ? <div>{`${site} marked this stay cancelled.`}</div> : null}
        {review.proposed.restore ? <div>This stay was cancelled in Lobbero. The booking site lists it again.</div> : null}
        {review.event && (review.event.external_ref || review.event.phone_last4_masked) ? (
          <div>
            {[
              review.event.external_ref ? `Reservation code ${review.event.external_ref}` : '',
              review.event.phone_last4_masked ? `Phone ends ${review.event.phone_last4_masked}` : '',
            ].filter(Boolean).join(' · ')}
          </div>
        ) : null}
        {review.reservation ? <div>{`Guest: ${review.reservation.guest_display}`}</div> : null}
        {!pending ? <div>{decidedLine(review)}</div> : null}
      </Muted>

      {can && ask && isNewShape ? (
        <Field>
          <Label as="span">{`${site} shows these nights as closed. Is this a stay?`}</Label>
          <SegmentedControl
            label={`Is ${range} a stay or blocked dates?`}
            options={[
              { value: 'reservation', label: 'Stay' },
              { value: 'block', label: 'Blocked dates' },
            ]}
            value={as}
            onChange={v => setAs(v)}
          />
        </Field>
      ) : null}

      {can && kind === 'conflict' ? (
        <RowLine>
          <Field>
            <Label htmlFor={`${uid}-in`}>Check-in</Label>
            <Input
              id={`${uid}-in`}
              type="date"
              value={dates.check_in}
              hasError={!!errors.check_in}
              onChange={e => setDates(d => ({ ...d, check_in: e.target.value }))}
            />
            <FieldError message={errors.check_in} />
          </Field>
          <Field>
            <Label htmlFor={`${uid}-out`}>Check-out</Label>
            <Input
              id={`${uid}-out`}
              type="date"
              value={dates.check_out}
              min={dates.check_in}
              hasError={!!errors.check_out}
              onChange={e => setDates(d => ({ ...d, check_out: e.target.value }))}
            />
            <FieldError message={errors.check_out} />
          </Field>
        </RowLine>
      ) : null}

      {can && isNewShape && as === 'reservation' ? (
        <>
          <div>
            <AppButton
              variant="ghost"
              size="compact"
              type="button"
              aria-expanded={guestOpen}
              aria-controls={`${uid}-guest`}
              aria-label={`Add guest details for ${range}`}
              onClick={() => setGuestOpen(o => !o)}
            >
              {guestOpen ? 'Hide guest details' : 'Add guest details'}
            </AppButton>
          </div>
          <Collapse open={guestOpen} id={`${uid}-guest`}>
            <RowLine>
              <Field>
                <Label htmlFor={`${uid}-first`}>First name</Label>
                <Input id={`${uid}-first`} value={guest.lead_first_name} maxLength={100} autoComplete="off"
                  hasError={!!errors.lead_first_name} onChange={e => setG('lead_first_name', e.target.value)} />
                <FieldError message={errors.lead_first_name} />
              </Field>
              <Field>
                <Label htmlFor={`${uid}-last`}>Last name</Label>
                <Input id={`${uid}-last`} value={guest.lead_last_name} maxLength={100} autoComplete="off"
                  hasError={!!errors.lead_last_name} onChange={e => setG('lead_last_name', e.target.value)} />
                <FieldError message={errors.lead_last_name} />
              </Field>
              <Field>
                <Label htmlFor={`${uid}-email`}>Email</Label>
                <Input id={`${uid}-email`} type="email" inputMode="email" value={guest.lead_email} autoComplete="off"
                  hasError={!!errors.lead_email} onChange={e => setG('lead_email', e.target.value)} />
                <FieldError message={errors.lead_email} />
              </Field>
              <Field>
                <Label htmlFor={`${uid}-phone`}>Phone</Label>
                <Input id={`${uid}-phone`} type="tel" inputMode="tel" value={guest.lead_phone} maxLength={32} autoComplete="off"
                  hasError={!!errors.lead_phone} onChange={e => setG('lead_phone', e.target.value)} />
                <FieldError message={errors.lead_phone} />
              </Field>
              <Field>
                <Label htmlFor={`${uid}-party`}>Guests</Label>
                <Input id={`${uid}-party`} type="number" inputMode="numeric" min={1} max={30} value={guest.party_size}
                  hasError={!!errors.party_size} onChange={e => setG('party_size', e.target.value)} />
                <FieldError message={errors.party_size} />
              </Field>
            </RowLine>
            <Field>
              <Label htmlFor={`${uid}-notes`}>Notes</Label>
              <CountedTextarea id={`${uid}-notes`} value={guest.notes} max={500} hasError={!!errors.notes}
                onChange={e => setG('notes', e.target.value)} />
              <FieldError message={errors.notes} />
            </Field>
          </Collapse>
        </>
      ) : null}

      {primary ? (
        <Actions>
          <ActionWithReason reason={needsDates ? 'Change the dates first — these nights are taken' : null}>
            {describedBy => (
              <AppButton
                variant={primary.variant}
                size="compact"
                type="button"
                loading={busy === 'approve'}
                loadingLabel="Saving…"
                disabled={needsDates || (!!busy && busy !== 'approve')}
                aria-describedby={describedBy}
                aria-label={primary.aria}
                onClick={() => (primary.variant === 'destructive' ? setConfirmCancel(true) : void send('approve'))}
              >
                {primary.label}
              </AppButton>
            )}
          </ActionWithReason>
          <AppButton
            variant={secondary.variant}
            size="compact"
            type="button"
            loading={busy === 'dismiss'}
            loadingLabel="Saving…"
            disabled={!!busy && busy !== 'dismiss'}
            aria-label={secondary.aria}
            onClick={() => void send('dismiss')}
          >
            {secondary.label}
          </AppButton>
        </Actions>
      ) : null}

      <ConfirmDialog
        open={confirmCancel}
        title={`Cancel the stay ${formatDayKey(dates.check_in)} → ${formatDayKey(dates.check_out)}?`}
        message={`The stay is cancelled in Lobbero. Its check-in link stops working. ${site} is not changed.`}
        primaryLabel="Cancel the stay"
        primaryVariant="destructive"
        onPrimary={async () => {
          setConfirmCancel(false);
          await send('approve');
        }}
        dismissLabel="Keep it"
        onDismiss={() => setConfirmCancel(false)}
      />
    </Card>
  );
};
